import React, { useEffect, useState } from "react";
import Big from "big.js";
import { useDebounce } from "react-use";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { NumberFormatValues, SourceInfo } from "react-number-format";
import {
  SCard,
  SCardColumn1,
  SCardColumn2,
  SCardRow1,
  SCardRow2,
  SCardRow3,
  SCardRow4,
  SCardRow5,
  SCardTitle,
  SCardSubTitle,
  SCardLabelPercentage,
  SCardLabelAmount,
  SCardPercentageSlider,
  SCardInputAmount,
  SCardHomeIcon,
  SCardCheckBox,
  SCardHogIcon,
  SCardBagIcon,
  SCardBuildIcon,
  SCardWrapper,
  SAssetControls,
  SDeleteButton,
  STrashCanIcon,
  SCardInputPercentage,
  // SLockButton,
  // SLockIcon,
} from "./styles";
import { AssetData, useCurrentPortfolio } from "../../../../store";
import { CurrencyInput, PercentageInput } from "../..";
import { translateCategory } from "../../../../utils";

interface PortfolioEditorAssetItemPropsType {
  asset: AssetData;
  assetKey: string;
}

interface maskedInputValueType {
  formattedValue: string;
  value: string;
  floatValue: number;
}

export const PortfolioEditorAssetItem: React.FC<
  PortfolioEditorAssetItemPropsType
> = ({ asset, assetKey }) => {
  const { t } = useTranslation();
  const [isCardSelected, setIsCardSelected] = useState(false);
  const [assetInvestmentValue, setAssetInvestmentValue] = useState(
    asset.amount
  );
  const [sliderValue, setSliderValue] = useState(asset.percentage);
  const [assetPercentage, setAssetPercentage] = useState(asset.percentage);
  const assetType = translateCategory(asset.asset.portfolioCategory);
  const {
    updatePortfolioAsset,
    removePortfolioAsset,
    removeCurrentPortfolio,
    isLastAssetInPortfolio,
    isLastPortfolio,
  } = useCurrentPortfolio();
  const navigate = useNavigate();

  const handleCardSelected = (): void => {
    setIsCardSelected((currentCardSelected) => !currentCardSelected);
  };

  const handleDeleteAsset = (): void => {
    removePortfolioAsset(assetKey);
    if (isLastPortfolio && isLastAssetInPortfolio) {
      navigate("../");
    }
    if (isLastAssetInPortfolio) {
      removeCurrentPortfolio();
    }
  };

  // const handleLockAsset = (): void => {
  //   // TODO: lock actions
  // };

  const handleAssetPercentage = (
    maskedInputValue: maskedInputValueType,
    sourceInfo: SourceInfo
  ): void => {
    // onValueChange is called on EVERY change (unlike onChange) not only on events.
    // When value is fully deleted by the user, sourceinfo.source equals to "props" and not "event"
    if (
      sourceInfo.source === "event" ||
      (sourceInfo.source === "prop" && maskedInputValue.floatValue === 0)
    ) {
      const percentValue = maskedInputValue.floatValue;
      setAssetPercentage(percentValue);
    }
  };

  const handleAssetValue = (
    maskedInputValue: NumberFormatValues,
    sourceInfo: SourceInfo
  ): void => {
    // onValueChange is called on EVERY change (unlike onChange) not only on events.
    // When value is fully deleted by the user, sourceinfo.source equals to "props" and not "event"
    if (
      sourceInfo.source === "event" ||
      (sourceInfo.source === "prop" && maskedInputValue.floatValue === 0)
    ) {
      const amountValue = maskedInputValue.floatValue;
      setAssetInvestmentValue(amountValue || 0);
    }
  };

  const handleSlide = (_event: Event, newValue: number | number[]): void => {
    setSliderValue(Number(newValue));
  };

  useDebounce(
    () => {
      setAssetPercentage(Number(sliderValue));
    },
    10,
    [sliderValue]
  );

  useDebounce(
    () => {
      if (assetInvestmentValue !== asset.amount) {
        updatePortfolioAsset(
          "RULE_ADD_ASSET_INVESTMENT_VALUE",
          asset.asset,
          null,
          assetInvestmentValue
        );
      }
    },
    1250,
    [assetInvestmentValue]
  );

  useDebounce(
    () => {
      if (assetPercentage !== asset.percentage) {
        setSliderValue(
          Big(assetPercentage || 0)
            .round(2)
            .toNumber()
        );
        updatePortfolioAsset(
          "RULE_EDIT_ASSET_PERCENTAGE",
          asset.asset,
          assetPercentage,
          null
        );
      }
    },
    1250,
    [assetPercentage]
  );

  useEffect(() => {
    setAssetPercentage(asset.percentage);
    setSliderValue(asset.percentage || 0);
  }, [asset.percentage]);

  useEffect(() => {
    setAssetInvestmentValue(asset.amount);
  }, [asset.amount]);

  return (
    <SCardWrapper>
      <SCard $selected={isCardSelected}>
        <SCardColumn1>
          {asset.asset.portfolioCategory === "ACAO" && <SCardBagIcon />}
          {asset.asset.portfolioCategory === "FUNDO" && <SCardHomeIcon />}
          {asset.asset.portfolioCategory === "EUROPA" && <SCardHomeIcon />}
          {asset.asset.portfolioCategory === "RENDA_FIXA" && <SCardHogIcon />}
          {asset.asset.portfolioCategory === "PREVIDENCIA" && (
            <SCardBuildIcon />
          )}
        </SCardColumn1>

        <SCardColumn2>
          <SCardRow1>
            <SCardTitle>{asset.asset.label}</SCardTitle>
            <SCardCheckBox
              checked={isCardSelected}
              onChange={handleCardSelected}
            />
          </SCardRow1>
          <SCardRow2>
            <SCardSubTitle>{assetType}</SCardSubTitle>
          </SCardRow2>
          <SCardRow3>
            <SCardLabelPercentage>
              {t("assetItem.walletPercentage")}
            </SCardLabelPercentage>
            <SCardLabelAmount>
              {t("assetItem.investmentAmount")}
            </SCardLabelAmount>
          </SCardRow3>
          <SCardRow4>
            <PercentageInput
              $selected={isCardSelected}
              value={Big(assetPercentage || 0)
                .round(2)
                .times(100)
                .toNumber()}
              onValueChange={handleAssetPercentage}
              customInput={SCardInputPercentage}
              disableUnderline
            />

            <CurrencyInput
              value={Big(assetInvestmentValue || 0).toNumber()}
              onValueChange={handleAssetValue}
              customInput={SCardInputAmount}
              $selected={isCardSelected}
              disableUnderline
            />
          </SCardRow4>
          <SCardRow5>
            <SCardPercentageSlider
              min={0}
              max={100}
              onChange={handleSlide}
              value={Big(sliderValue || 0)
                .round(2)
                .toNumber()}
            />
          </SCardRow5>
        </SCardColumn2>
      </SCard>
      {isCardSelected && (
        <SAssetControls>
          {/* <SLockButton onClick={handleLockAsset}>
            <SLockIcon />
            {t("assetItem.lock")}
          </SLockButton> */}
          <SDeleteButton onClick={handleDeleteAsset}>
            <STrashCanIcon />
            {t("assetItem.delete")}
          </SDeleteButton>
        </SAssetControls>
      )}
    </SCardWrapper>
  );
};
